import { createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react';
import { Elements, Uuid } from '../../models/common';
import {
  AndroidVersion,
  App,
  AppCategory,
  AppCollection,
  AppMarketPermission,
  AppSearchAutoComplete,
  AppSearchInfo,
  CategoryApp,
  DeveloperAppReview,
  GlobalAppCollection,
  Merchant,
  MerchantBillingInformation,
  MerchantPlan,
  ServerFeature,
  SuggestedApp,
  MerchantPlanMeta
} from '../../models/app';
import { parseLocale } from '../../utils/i18n/locale';
import i18n from '../../i18n';
import { ApiQuery, getAppMarketContextForApi, getFetchArgs } from '../../utils/api';
import { EntityState, SerializedError } from '@reduxjs/toolkit';
import { SUGGESTED_APPS_LIMIT } from '../../utils/constants';
import { AppMode, ServerFeatureType } from '../../models/enums';
import {
  AppListBaseParams,
  AppReviewsParameters,
  AppsByDeveloperIdParameters,
  AppsInCategoryParameters,
  AppsInCollectionParameters,
  AppVersionsParameters,
  ChangeSubscriptionParams,
  DeleteReviewParams,
  FlagReviewParams,
  InstallAppParams,
  InstallUninstallAppParams,
  SearchAppsWithSearchStringParameters,
  ServerFeatureParams,
  SubmitReviewParams,
  SuggestAppParams,
  SuggestAppsParameters,
  SuggestedAppsTransformed,
  AppCollectionParameter,
} from './types';
import * as Adapters from './adapters';
import { showHideGlobalNotification } from '../reducers/app-market-slice';
import {
  AppInstallResponse,
  ChangeSubscriptionResponse,
  DeleteReviewResponse,
  FlagReviewResponse,
  SubmitReviewResponse,
  SubmitSuggestAppResponse,
  UninstallAppResponse,
} from '../../models/post-req-responses-payloads';
import { BaseQueryApi, BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {
  ApiCacheTag,
  apiTagCreator,
  getAppCollectionScenarioTagId,
  getInstallUninstallInvalidateTags,
} from './tags';
import { getAppMarketMode, isPrivateApp } from '../../utils/session';
import {
  getAppCategoriesQuery,
  getAppCollectionsQuery,
  getAppMarketPermissionsQuery,
  getAppsInCollectionQueries,
  getMerchantBillingQuery,
  getMerchantQuery,
  getRatingsAndReviewsQuery,
} from '../../utils/api-queries';
import { NotificationVariant } from '../../components/Common/Notifications/types';

let logoutDispatch: () => void;
export const webPortalLogoutBridge = (logout: Nullable<() => {}>) => {
  logoutDispatch = () => {
    if (logout) logout();
  };
};

export type RTKQueryResponseType<T = unknown> =
  | { data: T }
  | { error: FetchBaseQueryError | SerializedError | string };

const DEFAULT_APP_EXPANDS = [
  'appLocales',
  'screenshots',
  'availableMetereds',
  'availableSubscriptions',
  'modules',
  'deviceTypes',
  'androidVersion.releaseNote',
  'currentSubscription',
  'billing',
];


/* Below logic is for an edge case, where APPMKT was picking up session
when logged in to the Internal Tools and was not able to load the APPMKT loggedout view*/
let baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>

if(isPrivateApp()){
   baseQuery = fetchBaseQuery();
}else{
  baseQuery = fetchBaseQuery({credentials:"omit"});
}

/*
 * Custom fetchQuery function, which is used here to intercept the Api calls
 * Intercept for 401s and dispatch logout in such case
 */
const baseQueryInterceptor = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: Obj<any>) => {
  const result = await baseQuery(args, api, extraOptions);
  const status = result.meta?.response?.status || result.error?.status || 0;
  const method = result.meta?.request?.method || '';
  let errorMessage: string = '';

  if (result.data && status === 200) return result;
  else if (status === 401 && getAppMarketMode() === AppMode.Private) {
    // note: this may no longer be necessary
    logoutDispatch();
    return {
      error: 'Unauthorized',
    };
  } else {
    const errorCode = result?.error?.status || status;
    try {
      if (typeof result?.error?.data === 'string') {
        errorMessage = result.error.data;
      } else {
        let dataObj = result.error?.data as Maybe<Obj<any>>;
        errorMessage = dataObj?.details || dataObj?.message || '';
      }
    } catch (e) {
      errorMessage = '';
    }
    errorMessage = errorMessage ? `${errorMessage}|${errorCode}` : `${status}`;
    if (method.toLowerCase() === 'get') {
      //Show global error if get call fails
      api.dispatch(
        showHideGlobalNotification({
          notificationProperties: {
            closeable: true,
            fixedPosition: true,
            closeTimer: false,
          },
          notificationData: {
            variant: NotificationVariant.Yellow,
            title: 'app_actions.banners.error_loading' || errorMessage,
          },
        }),
      );
    }
    return {
      error: errorMessage,
    };
  }
};

export const appMarketApiSlice = createApi({
  reducerPath: 'fromAppMarket',
  baseQuery: baseQueryInterceptor,
  tagTypes: [
    ApiCacheTag.AppData,
    ApiCacheTag.Reviews,
    ApiCacheTag.Collection,
    ApiCacheTag.AppsInCollection,
    ApiCacheTag.MyApps,
    ApiCacheTag.MerchantData,
  ],
  endpoints(builder) {
    return {
      fetchMerchant: builder.query<Merchant, Maybe<Uuid>>({
        query: (merchantId) => getFetchArgs(getMerchantQuery(merchantId)),
        transformResponse(response: Elements<Merchant>) {
          return response.elements[0];
        },
        providesTags: (data, error, args) => {
          return [apiTagCreator(ApiCacheTag.MerchantData)];
        },
      }),
      fetchMerchantPlans: builder.query<EntityState<MerchantPlan>, void>({
        query() {
          const { contextType, id } = getAppMarketContextForApi();
          const query: Partial<ApiQuery> = {
            namespace: 'v3',
            baseResource: contextType,
            nestedResources: ['current_merchant_plan'],
            baseResourceId: id,
            expands: ['appBundle', 'modules'],
            filters: ['active=true'],
          };
          return getFetchArgs(query);
        },
        transformResponse(response: MerchantPlanMeta) {
          const plans: MerchantPlan[] = response.merchantPlans?.elements?.map(plan => ({
            ...plan,
          }));

          return Adapters.merchantPlanAdapter.addMany(Adapters.merchantPlanAdapter.getInitialState(), plans);
        },
        keepUnusedDataFor: 300,
      }),
      fetchAppCollections: builder.query<EntityState<GlobalAppCollection>, AppCollectionParameter>({
        query: (appCollectionParameter) =>
          getFetchArgs(getAppCollectionsQuery(appCollectionParameter.scenario, appCollectionParameter.appId)),
        transformResponse(response: Elements<AppCollection>) {
          const appCollections: GlobalAppCollection[] = response.elements?.map(appCollection => ({
            ...appCollection,
            carouselApps: appCollection.carouselApps?.elements.map(app => ({
              ...app,
            })),
          }));
          return Adapters.appCollectionsAdapter.addMany(
            Adapters.appCollectionsAdapter.getInitialState(),
            appCollections
          );
        },
        providesTags: (data, error, args) => {
          return [apiTagCreator(ApiCacheTag.Collection, getAppCollectionScenarioTagId(args))];
        },
      }),
      fetchAppReviews: builder.query<EntityState<DeveloperAppReview>, AppReviewsParameters>({
        query(appReviewsParameters) {
          const query: Partial<ApiQuery> = {
            nestedResources: ['apps', 'reviews'],
            nestedResourcesIds: [appReviewsParameters.appId],
            limit: appReviewsParameters.limit,
            offset: appReviewsParameters.offset,
            noCache: true,
          };

          return getFetchArgs(query);
        },
        transformResponse(response: Elements<DeveloperAppReview>) {
          const appReviews: DeveloperAppReview[] = response.elements?.map(appReview => ({
            ...appReview,
          }));

          return Adapters.appReviewsAdapter.addMany(Adapters.appReviewsAdapter.getInitialState(), appReviews);
        },
        providesTags: (data, error, args) => {
          const { appId } = args;
          return [apiTagCreator(ApiCacheTag.Reviews, appId)];
        },
      }),
      fetchAppCategories: builder.query<EntityState<AppCategory>, Uuid>({
        query: (categoryId: Uuid = '') => getFetchArgs(getAppCategoriesQuery(categoryId)),
        transformResponse(response: Elements<AppCategory>) {
          const appCategories: AppCategory[] = response.elements
            ?.filter(cat => cat.name !== 'FTU_EMPLOYEES')
            .map(appCategory => ({
              ...appCategory,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          return Adapters.appCategoriesAdapter.addMany(
            Adapters.appCategoriesAdapter.getInitialState(),
            appCategories
          );
        },
      }),
      fetchApp: builder.query<App, Uuid>({
        query(id: Uuid) {
          const query: Partial<ApiQuery> = {
            nestedResources: ['apps', id],
            expands: DEFAULT_APP_EXPANDS,
            customQueries: [
              {
                clientCountry: i18n?.language ? parseLocale(i18n?.language).country : 'US',
              },
            ],
          };

          return getFetchArgs(query);
        },
        providesTags: (data, error, args) => {
          return [apiTagCreator(ApiCacheTag.AppData, args || '')];
        },
      }),
      fetchAppsByDeveloperId: builder.query<EntityState<App>, AppsByDeveloperIdParameters>({
        query(appsByDeveloperIdParameters) {
          const query: Partial<ApiQuery> = {
            nestedResources: ['apps'],
            filters: [
              'approvalStatus=PUBLISHED',
              'hidden=false',
              `developer.id=${appsByDeveloperIdParameters.devId}`,
            ],
            expands: ['appLocales', 'availableMetereds', 'availableSubscriptions', 'modules'],
            customQueries: [
              {
                clientCountry: i18n?.language ? parseLocale(i18n?.language).country : 'US',
              },
            ],
            limit: appsByDeveloperIdParameters.limit,
          };
          if (appsByDeveloperIdParameters.sorts) query.sorts = appsByDeveloperIdParameters.sorts;

          return getFetchArgs(query);
        },
        transformResponse(response: Elements<App>) {
          return Adapters.appsAdapter.addMany(Adapters.appsAdapter.getInitialState(), response.elements);
        },
      }),
      fetchAppVersions: builder.query<EntityState<AndroidVersion>, AppVersionsParameters>({
        query(appVersionsParameters: AppVersionsParameters) {
          const query: Partial<ApiQuery> = {
            baseResource: 'apps',
            baseResourceId: appVersionsParameters.id,
            nestedResources: ['android_versions'],
            expands: ['releaseNote'],
            filters: ['approvalStatus%3DPUBLISHED'],
            limit: appVersionsParameters.limit,
          };

          return getFetchArgs(query);
        },
        transformResponse(response: Elements<AndroidVersion>) {
          return Adapters.appVersionsAdapter.addMany(
            Adapters.appVersionsAdapter.getInitialState(),
            response.elements
          );
        },
      }),
      fetchAppsInCollections: builder.query<EntityState<App>, AppsInCollectionParameters>({
        query: (appsInCollectionParameters) => getFetchArgs(getAppsInCollectionQueries(appsInCollectionParameters)),

        transformResponse(response: Elements<App>) {
          const apps: App[] = response.elements?.map(app => ({
            ...app,
          }));
          return Adapters.appsAdapter.addMany(Adapters.appsAdapter.getInitialState(), apps);
        },
        providesTags: (data, error, args) => {
          return [apiTagCreator(ApiCacheTag.AppsInCollection, args.appCollectionUuid)];
        },
      }),
      fetchAppsInCategory: builder.query<EntityState<App>, AppsInCategoryParameters>({
        query(appsInCategoryParameters) {
          const query: Partial<ApiQuery> = {
            baseResource: 'apps',
            baseResourceId: '',
            nestedResources: ['categories', appsInCategoryParameters.categoryId, 'associations'],
            expands: ['appLocales', 'availableMetereds', 'availableSubscriptions', 'modules'],
            customQueries: [
              {
                clientCountry: i18n?.language ? parseLocale(i18n?.language).country : 'US',
              },
            ],
            limit: appsInCategoryParameters.limit,
          };
          if (appsInCategoryParameters.sorts) query.sorts = appsInCategoryParameters.sorts;

          return getFetchArgs(query);
        },
        transformResponse(response: Elements<CategoryApp>) {
          const apps: App[] = response.elements?.map((categoryApp) => ({ ...categoryApp.app }));
          return Adapters.appsAdapter.addMany(Adapters.appsAdapter.getInitialState(), apps);
        },
      }),
      fetchAppsBySearchString: builder.query<EntityState<App>, SearchAppsWithSearchStringParameters>({
        query(searchAppsParameters) {
          const query: Partial<ApiQuery> = {
            baseResource: 'search',
            baseResourceId: 'apps',
            finds: [
              `id LIKE%${searchAppsParameters.q}%`,
              `name LIKE%${searchAppsParameters.q}%`,
              `description LIKE%${searchAppsParameters.q}%`,
              `developer.name LIKE%${searchAppsParameters.q}%`,
            ],
            customQueries: [
              {
                clientCountry: i18n?.language ? parseLocale(i18n?.language).country : 'US',
              },
              {
                mId: searchAppsParameters.merchantId ? searchAppsParameters.merchantId : ''
              }
            ],
            limit: searchAppsParameters.limit,
          };
          if (searchAppsParameters.sorts) query.sorts = searchAppsParameters.sorts;

          return getFetchArgs(query);
        },
        transformResponse(response: Elements<App>) {
          const appsArr: App[] = response.elements?.map(app => ({
            ...app,
          }));
          return Adapters.appsAdapter.addMany(Adapters.appsAdapter.getInitialState(), appsArr);
        },
      }),
      fetchInstalledApps: builder.query<EntityState<App>, AppListBaseParams>({
        query({ limit = 60, offset = 0, sorts, filters = [] }) {
          const query: Partial<ApiQuery> = {
            nestedResources: ['apps'],
            expands: ['appLocales', 'currentSubscription', 'availableMetereds', 'availableSubscriptions'],
            customQueries: [
              {
                clientCountry: i18n?.language ? parseLocale(i18n?.language).country : 'US',
              },
            ],
            limit,
            offset,
            filters,
            sorts,
          };

          return getFetchArgs(query);
        },
        providesTags: (data, error, args) => {
          return [apiTagCreator(ApiCacheTag.MyApps)];
        },
        transformResponse(response: Elements<App>) {
          return Adapters.appsAdapter.addMany(
            Adapters.appsAdapter.getInitialState(),
            response.elements || []
          );
        },
      }),
      fetchSuggestedApps: builder.query<SuggestedAppsTransformed, SuggestAppsParameters>({
        query(SuggestAppsParameters) {
          const query: Partial<ApiQuery> = {
            baseResource: 'suggest',
            baseResourceId: 'apps',
            finds: [`id LIKE%${SuggestAppsParameters.q}%`, `name LIKE%${SuggestAppsParameters.q}%`],
            customQueries: [
              {
                clientCountry: i18n?.language ? parseLocale(i18n?.language).country : 'US',
              },
              {
                mId: SuggestAppsParameters.merchantId ? SuggestAppsParameters.merchantId : ''
              },
              { q: SuggestAppsParameters.q || '' },
            ],
            limit: SUGGESTED_APPS_LIMIT,
          };
          return getFetchArgs(query);
        },
        transformResponse(response: AppSearchAutoComplete) {
          const suggestedApps: SuggestedApp[] = response.records.elements.map((suggestedApp) => ({
            ...suggestedApp
          }));
          const info: Maybe<Partial<AppSearchInfo>> = response.info;
          return {
            info,
            suggestedApps: Adapters.suggestedAppsAdapter.addMany(
              Adapters.suggestedAppsAdapter.getInitialState(),
              suggestedApps
            ),
          };
        },
      }),
      fetchServerFeatures: builder.query<EntityState<ServerFeature>, ServerFeatureParams>({
        query(serverFeatures) {
          const getFeaturesString = (featuresArr: ServerFeatureType[]): string => {
            let response = '',
              len = featuresArr.length;
            for (let i = 0; i < len; i++) {
              response += `"${featuresArr[i]}"`;
              if (i !== len - 1) response += ',';
            }
            return response;
          };
          const query: Partial<ApiQuery> = {
            nestedResources: ['server_features'],
            filters: [`name IN (${getFeaturesString(serverFeatures.features)})`],
          };
          return getFetchArgs(query);
        },
        transformResponse(response: Elements<ServerFeature>) {
          const featureArr: ServerFeature[] = response.elements?.map(feature => ({
            ...feature,
          }));
          return Adapters.serverFeatureAdapter.addMany(
            Adapters.serverFeatureAdapter.getInitialState(),
            featureArr
          );
        },
      }),

      fetchRatingsAndReviewsFeature: builder.query<ServerFeature, void>({
        query: () => getFetchArgs(getRatingsAndReviewsQuery()),
      }),

      fetchBillingInfo: builder.query<MerchantBillingInformation, void>({
        query: () => getFetchArgs(getMerchantBillingQuery()),
      }),
      fetchAppMarketPermissions: builder.query<EntityState<AppMarketPermission>, Uuid>({
        query: (merchantId) => getFetchArgs(getAppMarketPermissionsQuery(merchantId)),
        transformResponse(response: Elements<AppMarketPermission>) {
          const permissions: AppMarketPermission[] = response.elements?.map(permission => ({
            ...permission,
          }));
          return Adapters.permissionAdapter.addMany(
            Adapters.permissionAdapter.getInitialState(),
            permissions
          );
        },
      }),
      flagReview: builder.mutation<FlagReviewResponse, FlagReviewParams>({
        query(params) {
          const { appId, reviewId } = params;
          const flagAppReviewQuery: Partial<ApiQuery> = {
            baseResource: 'apps',
            baseResourceId: appId,
            nestedResources: ['reviews', 'flags'],
            nestedResourcesIds: [reviewId],
          };

          return getFetchArgs(flagAppReviewQuery, 'POST');
        },
      }),
      submitReview: builder.mutation<SubmitReviewResponse, SubmitReviewParams>({
        query(params) {
          const { appId, body } = params;
          const reviewQuery: Partial<ApiQuery> = {
            nestedResources: ['apps', 'reviews'],
            nestedResourcesIds: [appId],
          };

          return getFetchArgs(reviewQuery, 'POST', body);
        },
        invalidatesTags: (data, error, args) => {
          if (error) return [];
          const { appId } = args;
          return [
            { type: ApiCacheTag.AppData, id: appId },
            { type: ApiCacheTag.Reviews, id: appId },
          ];
        },
      }),
      deleteReview: builder.mutation<DeleteReviewResponse, DeleteReviewParams>({
        query(params) {
          const { appId, reviewId } = params;
          const reviewQuery: Partial<ApiQuery> = {
            nestedResources: ['apps', 'reviews'],
            nestedResourcesIds: [appId, reviewId],
          };

          return getFetchArgs(reviewQuery, 'DELETE');
        },
        invalidatesTags: (data, error, args) => {
          if (error) return [];

          const { appId } = args;
          return [
            { type: ApiCacheTag.AppData, id: appId },
            { type: ApiCacheTag.Reviews, id: appId }
          ];
        },
      }),
      installApp: builder.mutation<AppInstallResponse, InstallAppParams>({
        query(params) {
          const { appId, body } = params;
          const installAppQuery: Partial<ApiQuery> = {
            namespace: 'v2',
            nestedResources: ['apps', 'install'],
            nestedResourcesIds: ['', appId],
          };

          return getFetchArgs(installAppQuery, 'POST', body);
        },
        invalidatesTags: (data, error, args) => {
          if (error) return [];
          const { appId, collectionsRelatedToApp } = args;
          return getInstallUninstallInvalidateTags(appId, collectionsRelatedToApp, 'install');
        },
      }),
      unInstallApp: builder.mutation<UninstallAppResponse, InstallUninstallAppParams>({
        query({ appId }) {
          const unInstallAppQuery: Partial<ApiQuery> = {
            namespace: 'v2',
            nestedResources: ['apps', 'uninstall'],
            nestedResourcesIds: ['', appId],
          };

          return getFetchArgs(unInstallAppQuery, 'POST');
        },
        invalidatesTags: (data, error, args) => {
          if (error) return [];
          const { appId, collectionsRelatedToApp } = args;
          return getInstallUninstallInvalidateTags(appId, collectionsRelatedToApp, 'uninstall');
        },
      }),
      changeSubscription: builder.mutation<ChangeSubscriptionResponse, ChangeSubscriptionParams>({
        query(params) {
          const { appId, appSubscription } = params;
          const changeSubscriptionQuery: Partial<ApiQuery> = {
            namespace: 'v2',
            nestedResources: ['apps', 'subscription'],
            nestedResourcesIds: [appId, appSubscription],
          };

          return getFetchArgs(changeSubscriptionQuery, 'POST');
        },
        invalidatesTags: (data, error, args) => {
          if (error) return [];

          const { appId } = args;
          return [apiTagCreator(ApiCacheTag.AppData, appId), apiTagCreator(ApiCacheTag.MyApps)];
        },
      }),
      submitAppSuggest: builder.mutation<SubmitSuggestAppResponse, SuggestAppParams>({
        query(params) {
          const { body } = params;
          const appSuggestQuery: Partial<ApiQuery> = {
            namespace: 'v2',
            baseResource: 'internal',
            baseResourceId: '',
            nestedResources: ['dashboard', 'form', 'waitlist'],
          };

          return getFetchArgs(appSuggestQuery, 'POST', body);
        },
      }),
    };
  },
});

export const {
  useFetchMerchantQuery,
  useFetchMerchantPlansQuery,
  useFetchAppCollectionsQuery,
  useFetchAppReviewsQuery,
  useFetchAppCategoriesQuery,
  useFetchAppQuery,
  useFetchAppsByDeveloperIdQuery,
  useFetchAppVersionsQuery,
  useFetchAppsInCollectionsQuery,
  useFetchAppsInCategoryQuery,
  useFetchAppsBySearchStringQuery,
  useFetchInstalledAppsQuery,
  useFetchSuggestedAppsQuery,
  useFetchServerFeaturesQuery,
  useFetchRatingsAndReviewsFeatureQuery,
  useFetchBillingInfoQuery,
  useFetchAppMarketPermissionsQuery,
  useFlagReviewMutation,
  useSubmitReviewMutation,
  useDeleteReviewMutation,
  useInstallAppMutation,
  useUnInstallAppMutation,
  useChangeSubscriptionMutation,
  useSubmitAppSuggestMutation,
} = appMarketApiSlice;
